import { HtmlFinalStep, HtmlStepperStep } from '@superfunnel/types';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useHtmlStepperEditor } from './HtmlStepperEditorContext';
import {
  ThumbnailHarvesterState,
  useThumbnailHarvester,
} from './useThumbnailHarvester';

interface ThumbnailHarvesterContextValue extends ThumbnailHarvesterState {
  /** Get the cached HTML snapshot for a step. Returns null if not yet captured. */
  getSnapshot: (
    stepId: string,
    stepType: 'step' | 'finalStep',
  ) => string | null;
  /** Request a specific step to be re-captured. Use 'wrapper' stepType to recapture all. */
  requestStepUpdate: (
    stepId: string,
    stepType: 'step' | 'finalStep' | 'wrapper',
  ) => void;
}

const ThumbnailHarvesterContext =
  createContext<ThumbnailHarvesterContextValue | null>(null);

/**
 * Provides thumbnail HTML snapshots to descendant StepThumbnail components.
 * Renders a single hidden "harvester" iframe that captures static HTML
 * for each step, eliminating the need for individual heavy iframes per thumbnail.
 *
 * Automatically detects form changes and queues a re-capture for the currently
 * active step. Wrapper changes trigger a full re-capture of all steps.
 */
export function ThumbnailHarvesterProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { iframe, data, state, quizId } = useHtmlStepperEditor();
  const { iframeSrc: rawIframeSrc } = iframe;
  const { form, website, formData } = data;
  const { selectedFormId } = state;

  const steps: HtmlStepperStep[] = form?.development?.steps ?? [];
  const finalSteps: HtmlFinalStep[] = form?.development?.finalSteps ?? [];

  // remove the editable=true flag from the iframe source
  const iframeSrc = useMemo(() => {
    if (!rawIframeSrc) return '';
    const url = new URL(rawIframeSrc);
    url.searchParams.delete('editable');
    return url.toString();
  }, [rawIframeSrc]);

  const { cache, harvesting, cacheVersion, iframeRef, requestStepUpdate } =
    useThumbnailHarvester({
      iframeSrc,
      form,
      website,
      selectedFormId,
      quizId,
      steps,
      finalSteps,
    });

  // -- Auto-detect form changes per step and queue updates for changed ones --
  // Track fingerprints for every step + wrapper to detect changes
  const prevFingerprintsRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!formData) return;

    const currentFingerprints = new Map<string, string>();

    // Wrapper
    const wrapperJson = JSON.stringify(formData.wrapper ?? null);
    currentFingerprints.set('wrapper', wrapperJson);

    // Regular steps
    for (const step of (formData.steps ?? []) as HtmlStepperStep[]) {
      currentFingerprints.set(`step:${step.id}`, JSON.stringify(step));
    }

    // Final steps
    for (const step of (formData.finalSteps ?? []) as HtmlFinalStep[]) {
      currentFingerprints.set(`finalStep:${step.id}`, JSON.stringify(step));
    }

    const prev = prevFingerprintsRef.current;

    // On the very first run, just store — no diff to compute
    if (prev.size === 0) {
      prevFingerprintsRef.current = currentFingerprints;
      return;
    }

    // Diff: find changed keys
    let wrapperChanged = false;
    const changedSteps: { stepId: string; stepType: 'step' | 'finalStep' }[] =
      [];

    for (const [key, value] of currentFingerprints) {
      if (prev.get(key) !== value) {
        if (key === 'wrapper') {
          wrapperChanged = true;
        } else {
          const [stepType, stepId] = key.split(':') as [
            'step' | 'finalStep',
            string,
          ];
          changedSteps.push({ stepId, stepType });
        }
      }
    }

    // Store current as previous for next comparison
    prevFingerprintsRef.current = currentFingerprints;

    // Queue updates
    if (wrapperChanged) {
      // Wrapper affects all steps — request full recapture
      console.log(
        '[ThumbnailHarvester] wrapper changed — recapturing all steps',
      );
      requestStepUpdate('', 'wrapper');
    } else {
      for (const { stepId, stepType } of changedSteps) {
        console.log(
          `[ThumbnailHarvester] ${stepType}:${stepId} changed — queuing recapture`,
        );
        requestStepUpdate(stepId, stepType);
      }
    }
  }, [formData, requestStepUpdate]);

  const getSnapshot = useCallback(
    (stepId: string, stepType: 'step' | 'finalStep'): string | null => {
      return cache.get(`${stepType}:${stepId}`) || null;
    },
    [cache],
  );

  const value = useMemo(
    (): ThumbnailHarvesterContextValue => ({
      cache,
      harvesting,
      cacheVersion,
      getSnapshot,
      requestStepUpdate,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cacheVersion, harvesting, getSnapshot, requestStepUpdate],
  );

  return (
    <ThumbnailHarvesterContext.Provider value={value}>
      {/* Hidden harvester iframe — in viewport (so Chrome doesn't throttle it)
          but invisible via opacity:0 and behind all content via z-index:-1 */}
      {iframeSrc && (
        <div
          className="pointer-events-none fixed top-0 left-0 overflow-hidden"
          style={{ width: 1, height: 1, opacity: 0, zIndex: -1 }}
          aria-hidden="true"
        >
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            title="Thumbnail harvester"
            style={{ width: 1440, height: 900 }}
            tabIndex={-1}
          />
        </div>
      )}
      {children}
    </ThumbnailHarvesterContext.Provider>
  );
}

/**
 * Access the thumbnail harvester context.
 * Must be used within a ThumbnailHarvesterProvider.
 */
export function useThumbnailSnapshots(): ThumbnailHarvesterContextValue {
  const ctx = useContext(ThumbnailHarvesterContext);
  if (!ctx) {
    throw new Error(
      'useThumbnailSnapshots must be used inside ThumbnailHarvesterProvider',
    );
  }
  return ctx;
}
